import React, { useEffect, useState } from 'react';
import { Video, Clock, Calendar, ArrowRight } from 'lucide-react';

const getTimeParts = (target) => {
  const now = new Date();
  const diffMs = target - now;
  if (diffMs <= 0) {
    return { diffMs, days: 0, hours: 0, minutes: 0, seconds: 0 };
  }
  const totalSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSeconds / (24 * 60 * 60));
  const hours = Math.floor((totalSeconds % (24 * 60 * 60)) / (60 * 60));
  const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
  const seconds = totalSeconds % 60;
  return { diffMs, days, hours, minutes, seconds };
};

const buildTargetDate = (scheduledDate, scheduledTime) => {
  if (!scheduledDate) return null;

  // `scheduledDate` often comes back as an ISO datetime with a `Z` suffix
  // (e.g. "2026-02-26T00:00:00.000000Z"). Using `new Date(scheduledDate)` can shift
  // the date based on the user's timezone, making the countdown look "in the past".
  // We treat it as a date-only value in local time.
  const dateStr = typeof scheduledDate === 'string'
    ? scheduledDate.slice(0, 10)
    : String(scheduledDate).slice(0, 10);

  const base = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(base.getTime())) return null;

  if (scheduledTime) {
    const [hRaw, mRaw] = String(scheduledTime).split(':');
    const h = Number(hRaw || 0);
    const m = Number(mRaw || 0);
    base.setHours(h, m, 0, 0);
  } else {
    base.setHours(0, 0, 0, 0);
  }

  return base;
};

const ConsultationCountdownBanner = ({ consultation, onViewConsultations }) => {
  const [timeLeft, setTimeLeft] = useState(() => {
    const target = buildTargetDate(consultation.scheduled_date, consultation.scheduled_time);
    return target ? getTimeParts(target) : { diffMs: -1, days: 0, hours: 0, minutes: 0, seconds: 0 };
  });

  useEffect(() => {
    const target = buildTargetDate(consultation.scheduled_date, consultation.scheduled_time);
    if (!target) {
      setTimeLeft({ diffMs: -1, days: 0, hours: 0, minutes: 0, seconds: 0 });
      return;
    }

    setTimeLeft(getTimeParts(target));

    const interval = setInterval(() => {
      setTimeLeft(getTimeParts(target));
    }, 1000);

    return () => clearInterval(interval);
  }, [consultation.scheduled_date, consultation.scheduled_time]);

  if (!consultation || !consultation.scheduled_date) return null;

  const targetDate = buildTargetDate(consultation.scheduled_date, consultation.scheduled_time);
  if (!targetDate || Number.isNaN(targetDate.getTime()) || timeLeft.diffMs <= 0) return null;

  const { days, hours, minutes, seconds } = timeLeft;

  const formattedDate = targetDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const formattedTime = targetDate.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

  const clientName = consultation.client?.name || 'Client';

  const pill = (value, label) => (
    <div className="flex flex-col items-center justify-center w-14 sm:w-16">
      <div className="w-full rounded-lg bg-surface/80 shadow-sm border border-amber-100 py-1.5 sm:py-2 text-center transition-transform duration-200 transform-gpu hover:scale-105">
        <span className="block text-base sm:text-lg font-semibold text-amber-800 tabular-nums">
          {String(value).padStart(2, '0')}
        </span>
      </div>
      <span className="mt-1 text-[10px] sm:text-xs uppercase tracking-wide text-amber-700">
        {label}
      </span>
    </div>
  );

  return (
    <div className="mb-6 p-4 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 via-white to-orange-50 shadow-sm overflow-hidden relative animate-slide-up">
      <div className="pointer-events-none absolute -left-10 top-0 h-40 w-40 bg-amber-200/40 rounded-full blur-3xl" />
      <div className="pointer-events-none absolute -right-10 bottom-0 h-40 w-40 bg-orange-200/40 rounded-full blur-3xl" />

      <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="flex-shrink-0 p-2 sm:p-2.5 rounded-full bg-amber-100/90 shadow-inner animate-pulse-slow">
            <Video className="h-5 w-5 sm:h-6 sm:w-6 text-amber-700" />
          </div>
          <div>
            <h3 className="text-sm sm:text-base font-semibold text-amber-900 flex items-center gap-1.5">
              Upcoming Consultation with <span className="truncate">{clientName}</span>
            </h3>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs sm:text-sm text-amber-800">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                <span>{formattedDate}</span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                <span>{formattedTime}</span>
              </span>
            </div>
            <p className="mt-1.5 text-xs text-amber-700">
              Get ready to join your video consultation. Make sure your Meet link and notes are set.
            </p>
            <button
              type="button"
              onClick={onViewConsultations}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-amber-800 hover:text-amber-900"
            >
              Manage consultations
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <div className="flex flex-col items-center gap-2 sm:items-end">
          <p className="text-[11px] sm:text-xs font-medium text-amber-800 uppercase tracking-wide">
            Starts in
          </p>
          <div className="flex items-end gap-2 sm:gap-3">
            {pill(days, 'Days')}
            {pill(hours, 'Hours')}
            {pill(minutes, 'Mins')}
            {pill(seconds, 'Secs')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultationCountdownBanner;

